import { tz } from "@date-fns/tz";
import { type ContextFn, format } from "date-fns";
import { enUS, id, type Locale } from "date-fns/locale";

// * for user preferencecs
export const USER_PREF_STORAGE_KEY = "user-preferences";
// the date provider
export const DEFAULT_TIMEZONE = "Asia/Jakarta";
export const DEFAULT_TIME_FORMAT_12 = "12-hours" as const;
export const DEFAULT_TIME_FORMAT_24 = "24-hours" as const;
export const DEFAULT_TIME_FORMAT_12_DATE_FNS = "hh:mm a";
export const DEFAULT_TIME_FORMAT_24_DATE_FNS = "HH:mm";
export type TimeFormat =
	| typeof DEFAULT_TIME_FORMAT_12
	| typeof DEFAULT_TIME_FORMAT_24;
export type TimeFormatDateFns =
	| typeof DEFAULT_TIME_FORMAT_12_DATE_FNS
	| typeof DEFAULT_TIME_FORMAT_24_DATE_FNS;
// the theme provider
export const DEFAULT_THEME = "system";
export type Theme = "dark" | "light" | "system";
// the motion provider
export const DEFAULT_MOTION = "system";
export type MotionPreference = "on" | "off" | "system";
// the local storage for user preferences
export const DEFAULT_USER_PREFERENCES = {
	theme: DEFAULT_THEME as Theme,
	motion: DEFAULT_MOTION as MotionPreference,
	timeFormat: DEFAULT_TIME_FORMAT_12 as TimeFormat,
};
export type UserPreferences = typeof DEFAULT_USER_PREFERENCES;

export const SIDEBAR_WIDTH = "16rem";
export const SIDEBAR_WIDTH_MOBILE = "18rem";

export const INTERVAL_TOPBAR_DATE = 1;
export const LOCALES = { enUS, id };
export const CURRENT_DATE_TOPBAR = ({
	locale = enUS,
	timezone = tz(DEFAULT_TIMEZONE),
}: {
	locale?: Locale;
	timezone?: ContextFn<Date>;
}) => {
	const timeLabel = locale.code === "id" ? "pukul" : "at";

	return format(new Date(), `PPPP '${timeLabel}' p`, { locale, in: timezone });
};

export const ADMIN_TYPES = [
	"SUPER_ADMIN",
	"ADMIN",
	"ADMIN_SUPERVISOR",
] as const;

export const GENDERS = ["MALE", "FEMALE"] as const;

/** Codes accepted by fisiohome-backend's CHECK therapists_religion_known (#330). */
export const RELIGIONS = [
	"ISLAM",
	"PROTESTANT",
	"CATHOLIC",
	"HINDU",
	"BUDDHIST",
	"CONFUCIAN",
	"OTHER",
] as const;

export const GENDER_LABELS = GENDERS.map((p) => {
	const title = p;
	let titleId = "" as "LAKI-LAKI" | "PEREMPUAN";
	let titlePrefix = "" as "BAPAK" | "IBU";

	if (p === "MALE") {
		titleId = "LAKI-LAKI";
		titlePrefix = "BAPAK";
	}

	if (p === "FEMALE") {
		titleId = "PEREMPUAN";
		titlePrefix = "IBU";
	}

	return { title, titleId, titlePrefix };
});

export const EMPLOYMENT_STATUSES = ["ACTIVE", "HOLD", "INACTIVE"] as const;
export const EMPLOYMENT_TYPES = ["KARPIS", "FLAT"] as const;

export const DAY_NAMES = [
	"Sunday",
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
] as const;

export const PREFERRED_THERAPIST_GENDER = [
	"MALE",
	"FEMALE",
	"NO PREFERENCE",
] as const;

export const PREFERRED_THERAPIST_GENDER_LABELS = PREFERRED_THERAPIST_GENDER.map(
	(p) => {
		const title = p;
		let titleId = "";

		if (p === "NO PREFERENCE") {
			titleId = "Tidak ada preferensi";
		}

		if (p === "MALE") {
			titleId = "Laki-Laki";
		}

		if (p === "FEMALE") {
			titleId = "Perempuan";
		}

		return { title, titleId };
	},
);

export const PATIENT_REFERRAL_OPTIONS = [
	{ value: "INSTAGRAM", label: "Instagram" },
	{ value: "FACEBOOK", label: "Facebook" },
	{ value: "TIKTOK", label: "TikTok" },
	{ value: "YOUTUBE", label: "YouTube" },
	{ value: "IKLAN", label: "Iklan" },
	{ value: "GOOGLE", label: "Google" },
	{ value: "WEBSITE", label: "Website" },
	{ value: "WHATSAPP", label: "WhatsApp" },
	{
		value: "FAMILY OR CLOSE RELATED PERSON",
		label: "Keluarga, teman, atau orang dekat",
	},
	{ value: "DOCTOR OR HOSPITAL", label: "Dokter atau rumah sakit" },
	{ value: "OTHER", label: "Lainnya" },
] as const;

/**
 * Resolves a referral source code (e.g. `"INSTAGRAM"`) to its human-readable
 * label. Falls back to the raw value when no matching option is found.
 */
export const getPatientReferralLabel = (value?: string | null) =>
	PATIENT_REFERRAL_OPTIONS.find((option) => option.value === value)?.label ??
	value ??
	"";

export const FISIOHOME_PARTNER = [
	{ value: "COSMART", label: "Cosmart" },
	{ value: "KLINIKGO", label: "KlinikGo" },
	{ value: "THE ASIAN PARENT", label: "The Asian Parent" },
	{ value: "ORAMI CIRCLE", label: "Orami Circle" },
	{ value: "IBU2CANGGIH", label: "Ibu2canggih" },
	{ value: "IBU BAYI CANGGIH", label: "Ibu Bayi Canggih" },
	{ value: "KOMPAS MYVALUE", label: "Kompas myValue" },
	{ value: "BLIBLI", label: "Blibli" },
	{ value: "LOVECARE", label: "LoveCare" },
	{ value: "MEDLIFE", label: "Medlife" },
	{ value: "MEDIKIDS", label: "Medikids" },
	{ value: "BUMI HEALTH", label: "Bumi Health" },
	{ value: "OTHER", label: "Lainnya" },
] as const;

/**
 * Resolves a fisiohome partner code (e.g. `"COSMART"`) to its human-readable
 * label. Falls back to the raw value when no matching option is found.
 */
export const getFisiohomePartnerLabel = (value?: string | null) =>
	FISIOHOME_PARTNER.find((option) => option.value === value)?.label ??
	value ??
	"";

// * patient condition enums
export const PATIENT_CONDITIONS = [
	"NORMAL",
	"ONLY ABLE TO SIT",
	"BEDRIDDEN",
	"NOT YET INFORMED",
] as const;

export const PATIENT_CONDITIONS_WITH_DESCRIPTION = PATIENT_CONDITIONS.map(
	(condition) => {
		let title = "";
		let description = "";
		let titleId = "";
		let descriptionId = "";

		switch (condition) {
			case "NORMAL":
				title = condition;
				description = "Fully mobile with no restrictions.";
				titleId = "NORMAL";
				descriptionId = "Pasien dapat bergerak dengan bebas tanpa batasan";
				break;
			case "ONLY ABLE TO SIT":
				title = condition;
				description =
					"Limited mobility, the patient can sit but not stand or walk.";
				titleId = "HANYA BISA DUDUK";
				descriptionId =
					"Mobilitas terbatas, pasien hanya bisa duduk tapi tidak bisa berdiri atau berjalan";
				break;
			case "BEDRIDDEN":
				title = condition;
				description = "The patient is confined to bed.";
				titleId = "TIRAH BARING";
				descriptionId = "Pasien terbaring di tempat tidur";
				break;
			case "NOT YET INFORMED":
				title = condition;
				description =
					"The patient has not informed their condition. Please confirm with the patient before the visit.";
				titleId = "BELUM DIINFORMASIKAN PASIEN";
				descriptionId = "Mohon konfirmasi ke pasien sebelum visit";
				break;
		}

		return { title, description, titleId, descriptionId };
	},
);

// The stored condition's option, or undefined for a value no option spells (legacy rows).
export const getPatientCondition = (value?: string | null) =>
	PATIENT_CONDITIONS_WITH_DESCRIPTION.find((option) => option.title === value);

// * media query list
export const IS_DEKSTOP_MEDIA_QUERY = "only screen and (min-width : 1024px)";
export const IS_TABLET_MEDIA_QUERY =
	"only screen and (min-width : 768px) and (max-width : 1023px)";
export const IS_MOBILE_MEDIA_QUERY = "only screen and (max-width : 767px)";

// * date of birth calendar configuration
export const DOB_YEAR_RANGE = 150;
